import { useEffect, useMemo, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import Avatar from "components/avatar";
import { Checkbox, SearchInput } from "components/fields";
import { SegmentedControl } from "components/tabs";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";

const ROLE_OPTIONS = [
  { value: "", label: "All" },
  { value: "member", label: "Members" },
  { value: "volunteer", label: "Volunteers" },
];

/**
 * People picker for services. `draggable` renders drag sources for the
 * calendar's assign panel; otherwise rows are checkboxes bound to `selected`.
 */
const AssignForm = ({ draggable, selected, setSelected }) => {
  const [users, setUsers] = useState(selected || []);
  const [queryOptions, setQueryOptions] = useState({ per: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const query = {
          status: "active",
        };
        const response = await AtriumApi.get(
          `/v1/users?${new URLSearchParams(query).toString()}`,
          {
            params: queryOptions,
          }
        );
        if (response) {
          setUsers(response?.data?.users);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [queryOptions, reloadKey]);

  const sortedUsers = useMemo(() => {
    const missedUsers = selected?.filter(
      (user) => users?.findIndex((u) => u?.id === user?.id) === -1
    );
    return missedUsers.concat(users || []);
  }, [users, selected]);

  const isSelected = (user) => selected?.findIndex((u) => u?.id === user?.id) !== -1;

  const toggleUser = (user) => {
    const index = selected?.findIndex((u) => u?.id === user?.id);
    if (index === -1) {
      setSelected(selected?.concat([user]));
    } else {
      setSelected(selected?.slice(0, index).concat(selected?.slice(index + 1)));
    }
  };

  const renderUsers = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-3 pt-2" aria-busy="true">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex items-center gap-3 p-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton lines={2} className="flex-1" />
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <ErrorState
          message={getApiErrorMessage(error, "Could not load people.")}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      );
    }

    if (sortedUsers.length === 0) {
      return <EmptyState variant="inline" title="No people match your search" />;
    }

    if (draggable) {
      return (
        <ul className="flex flex-col gap-1">
          {sortedUsers.map((user) => (
            <li
              key={`user${user?.id}`}
              data-id={user?.id}
              data-type="user"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.clearData();
                e.dataTransfer.setData("text/plain", user?.id);
                e.dataTransfer.effectAllowed = "all";
              }}
              className="flex cursor-grab items-center gap-2 rounded-control p-2 transition-colors hover:bg-surface-subtle active:cursor-grabbing"
            >
              <Avatar user={user} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-fg">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs capitalize text-fg-muted">{user?.role}</p>
              </div>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <ul className="flex flex-col gap-1">
        {sortedUsers.map((user) => {
          const checked = isSelected(user);
          return (
            <li key={`user${user?.id}`}>
              <label
                className={`flex cursor-pointer items-center gap-3 rounded-control p-2 transition-colors hover:bg-surface-subtle ${
                  checked ? "bg-surface-subtle" : ""
                }`}
              >
                <Checkbox checked={checked} onChange={() => toggleUser(user)} />
                <Avatar user={user} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-fg">
                    {user?.firstName} {user?.lastName}
                  </span>
                  <span className="block text-xs capitalize text-fg-muted">{user?.role}</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <SearchInput
        value={queryOptions.q || ""}
        onChange={(q) => setQueryOptions((prev) => ({ ...prev, q }))}
        placeholder="Search for people…"
        ariaLabel="Search people"
      />

      <SegmentedControl
        aria-label="Filter by role"
        options={ROLE_OPTIONS}
        value={queryOptions.role || ""}
        onChange={(role) => setQueryOptions((prev) => ({ ...prev, role }))}
        className="self-start"
      />

      {renderUsers()}
    </div>
  );
};

export default AssignForm;
